// ────────────────────────────────────────────────────────────────────────
// Hook de datos de la vista Forecasting & Programación.
//
// Consume el microservicio de forecasting (microservice-forecasting) vía BFF.
// Tres llamadas, todas scoped por el backend según el rol del token:
//   1. getForecasts        → serie de proyección vigente (yhat + intervalos de predicción 80/95)
//   2. getForecastHistory  → backtest de la corrida vigente (real vs predicho, WAPE/sesgo/MAE)
//   3. listEvents          → calendario de eventos (globales + del tenant)
//
// La proyección y la precisión dependen de { metric, granularity } y se recargan
// al cambiarlos; los eventos son independientes de la métrica.
// ────────────────────────────────────────────────────────────────────────
import { useState, useEffect, useCallback, useRef } from "react";
import { getForecasts, getForecastHistory, listEvents } from "api/forecasting";
import { apiErrorMessage, apiErrorStatus } from "utils/apiErrorMessage";
import i18n from "../../../i18n/index.js";

// The backend body ({message, error, statusCode}) wins whenever it carries
// usable text; the per-status texts are only a fallback for payloads without
// one (network/proxy failures, HTML pages, bare 'Forbidden'). axios
// transport noise (err.message) must never reach the UI.
export function extractError(err) {
  const status = apiErrorStatus(err);
  let fallback = i18n.t("No se pudo cargar el forecast.");
  if (status === 401) fallback = i18n.t("Tu sesión expiró. Vuelve a iniciar sesión.");
  if (status === 403) fallback = i18n.t("No tienes permisos para ver esta proyección.");
  return apiErrorMessage(err, fallback);
}

// Ventana de precisión histórica por granularidad (días hacia atrás). Mensual: los
// 13 meses del backtest mensual. Es la misma ventana que usa el backend cuando no
// recibe `from` (api/forecasts.py): con 400 días el mes más viejo salía de la
// ventana a mitad de mes y el error típico mensual cambiaba sin que cambiara el
// modelo; 430 días cubren los 13 meses completos en cualquier día del mes.
export const HISTORY_WINDOW_DAYS = { daily: 90, weekly: 90, monthly: 430 };

export function historyFrom(granularity, now = new Date()) {
  const d = new Date(now);
  d.setDate(d.getDate() - (HISTORY_WINDOW_DAYS[granularity] || 90));
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function useForecastingData({ metric, granularity = "daily" } = {}) {
  const [state, setState] = useState({ loading: true, error: null, series: [], history: [] });
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  // Evita que una respuesta vieja (cambio rápido de métrica) pise a la nueva.
  const reqId = useRef(0);

  const load = useCallback(async () => {
    if (!metric) return;
    const myReq = ++reqId.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const [series, history] = await Promise.all([
        getForecasts({ metric, granularity }),
        // La precisión es opcional: si aún no hay corridas evaluadas, no es un error.
        getForecastHistory({ metric, granularity, from: historyFrom(granularity) }).catch(() => []),
      ]);
      if (myReq !== reqId.current) return;
      setState({ loading: false, error: null, series, history });
    } catch (err) {
      if (myReq !== reqId.current) return;
      setState({ loading: false, error: extractError(err), series: [], history: [] });
    }
  }, [metric, granularity]);

  const loadEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      setEvents(await listEvents({}));
    } catch {
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  return { ...state, reload: load, events, eventsLoading, reloadEvents: loadEvents };
}
